import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import {
  Box,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  Stack,
  SxProps,
  TextFieldProps,
  Typography,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

import {
  userForgotPassword,
  userResetPassword,
} from '../../store/AuthSlice/V2/AuthApi';
import { getAuthState } from '../../store/AuthSlice';
import { TP_COLORS } from '../../constants';
import { useAppSelector } from '../../hooks';
import { TPTextField } from '../../components/Inputs';
import Header from '../V2/Portal/components/Header';
import CodeInput from './components/CodeInput';

const boxStyles: SxProps = {
  minHeight: 'calc(100vh - 64px)',
  marginTop: '64px',
  background: `url(${process.env.PUBLIC_URL}/images/backgrounds/bg-reset-password.avif)`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const stackStyles: SxProps = {
  textAlign: 'center',
  padding: 4,
  maxWidth: 350,
  width: '100%',
};

const loadingButtonProps: LoadingButtonProps = {
  fullWidth: true,
  disableElevation: true,
  variant: 'contained',
  sx: {
    py: '13px',
    borderRadius: '35px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: TP_COLORS.gray.darken,
    '&:hover': {
      backgroundColor: '#11172b',
    },
  },
};

type TState = {
  code: string;
  newPassword: string;
  confirmPassword: string;
  isPasswordVisible: boolean;
  isConfirmPasswordVisible: boolean;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
};

const initialState: TState = {
  code: '',
  newPassword: '',
  confirmPassword: '',
  isPasswordVisible: false,
  isConfirmPasswordVisible: false,
};

const RESEND_TIMER = 60; // 60 seconds countdown

const ResetPassword: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const urlEmail = urlParams.get('email');

  // Store
  const { status } = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);
  const [resendTimer, setResendTimer] = useState(0);
  const [errors, setErrors] = useState({
    passwordMismatch: false,
    code: false,
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendTimer]);

  const isSubmitDisabled =
    state.code.length < 6 ||
    !state.newPassword ||
    state.newPassword !== state.confirmPassword;

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    updateStateAttributes({ [name]: value });

    // Check password match when either password field changes
    if (name === 'newPassword' || name === 'confirmPassword') {
      const newPassword = name === 'newPassword' ? value : state.newPassword;
      const confirmPassword =
        name === 'confirmPassword' ? value : state.confirmPassword;

      setErrors((prev) => ({
        ...prev,
        code: state.code.length < 6,
        passwordMismatch:
          newPassword !== confirmPassword && confirmPassword !== '',
      }));
    }
  };

  const togglePasswordVisibility = () => {
    updateStateAttributes({ isPasswordVisible: !state.isPasswordVisible });
  };

  const toggleConfirmPasswordVisibility = () => {
    updateStateAttributes({
      isConfirmPasswordVisible: !state.isConfirmPasswordVisible,
    });
  };

  const handleCodeChange = (code: string) => {
    updateStateAttributes({ code });
    setErrors((prev) => ({
      ...prev,
      code: false,
    }));
  };

  const onSendCode = () => {
    if (urlEmail) {
      dispatch(
        userForgotPassword({
          email: urlEmail,
        })
      );
      setResendTimer(RESEND_TIMER);
    }
  };

  const onSubmit = () => {
    if (urlEmail) {
      dispatch(
        userResetPassword({
          email: urlEmail,
          code: state.code,
          password: state.newPassword,
        })
      );
    }
  };

  return (
    <Box>
      <Header />
      <Box sx={boxStyles}>
        <Stack spacing={4} sx={stackStyles}>
          <Stack spacing="10px">
            <Typography fontSize="30px" fontWeight="700" lineHeight="36px">
              Reset Your Password
            </Typography>
            <Typography fontSize="14px" color="#647488" lineHeight="17px">
              We sent a code to {urlEmail}
            </Typography>
          </Stack>

          <Stack spacing="10px">
            <CodeInput onChange={handleCodeChange} />
            <Typography fontSize="14px" color="#647488" lineHeight="17px">
              Didn&apos;t receive the code ?{' '}
              <Typography
                component="span"
                fontSize="14px"
                sx={{
                  color:
                    resendTimer > 0
                      ? '#647488'
                      : TP_COLORS.tutopiyaGreenMain.darken,
                  textDecoration: 'underline',
                  cursor: resendTimer > 0 ? 'not-allowed' : 'pointer',
                  opacity: resendTimer > 0 ? 0.7 : 1,
                  '&:hover': {
                    opacity: resendTimer > 0 ? 0.7 : 0.8,
                  },
                }}
                onClick={resendTimer > 0 ? undefined : onSendCode}
              >
                {resendTimer > 0
                  ? `Send again (${resendTimer}s)`
                  : 'Send again'}
              </Typography>
            </Typography>
          </Stack>

          <Stack spacing={2} paddingBottom={4}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                type={state.isPasswordVisible ? 'text' : 'password'}
                name="newPassword"
                label="New Password"
                autoComplete="new-password"
                value={state.newPassword}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility}>
                        {state.isPasswordVisible ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                type={state.isConfirmPasswordVisible ? 'text' : 'password'}
                name="confirmPassword"
                label="Confirm Password"
                value={state.confirmPassword}
                onChange={handleInputChange}
                error={errors.passwordMismatch}
                helperText={
                  errors.passwordMismatch ? 'Passwords do not match' : ' '
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleConfirmPasswordVisibility}>
                        {state.isConfirmPasswordVisible ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Stack>

          <Stack spacing={1}>
            <LoadingButton
              {...loadingButtonProps}
              loading={status === 'loading'}
              disabled={isSubmitDisabled}
              onClick={onSubmit}
            >
              SUBMIT
            </LoadingButton>
            <Fade in={errors.code}>
              <Typography fontSize="14px" color="#647488" lineHeight="17px">
                Please fill in the code you received in your email
              </Typography>
            </Fade>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default ResetPassword;
